import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";
import { StatusCode } from "api/protocols";
import Page from "components/page/Page";
import DataTable from "components/dataTable/DataTable";
import Card from "components/card/Card";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { showError, showSuccess } from "redux/actions/ui";
import { useAppDispatch } from "hooks/redux";
import Input from "components/form/input/Input";
import "./categories.scss";
import classNames from "classnames";
import {
  GetAccommodationCategories,
  PostAccommodationCategory,
  PutAccommodationCategory,
} from "api/rpc/2024-04/facilityAdmin/accommodation/categories";

export interface IAccommodationCategory {
  id: number;
  title: string;
}

export function Categories() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [accommodationCategories, setAccommodationCategories] = useState<IAccommodationCategory[] | undefined>(
    undefined,
  );
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCategoryTitle, setNewCategoryTitle] = useState("");
  const [editingCategoryId, setEditingCategoryId] = useState<number | null>(null);
  const [editedTitle, setEditedTitle] = useState("");

  const [filterState, setFilterState] = useState({
    search: "",
    offset: 0,
    limit: 10,
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadAccommodationCategories(source?.token);

    return () => {
      source?.cancel();
    };
  }, [filterState]);

  async function loadAccommodationCategories(token?: CancelToken) {
    if (accommodationCategories !== undefined) {
      setAccommodationCategories(undefined);
    }
    const accommodationCategoriesRes = await GetAccommodationCategories(null, false);

    if (token && token?.reason) {
      return;
    }
    if (accommodationCategoriesRes?.status !== StatusCode?.OK) {
      setAccommodationCategories([]);
      return;
    }

    setAccommodationCategories(accommodationCategoriesRes?.data as IAccommodationCategory[]);
  }

  const handleSaveNewCategory = async () => {
    const categoryRes = await PostAccommodationCategory(
      {
        title: newCategoryTitle?.trim(),
      },
      true,
    );

    if (categoryRes?.status !== StatusCode?.OK) {
      dispatch(showError("Error creating new category"));
      return;
    }

    dispatch(showSuccess(t("Successfully created category")));
    setNewCategoryTitle("");
    setIsAddingCategory(false);
    void loadAccommodationCategories();
  };

  const handleSaveEditedCategory = async (category: IAccommodationCategory) => {
    const updated = { ...category, title: editedTitle?.trim() };

    // Example API call (replace with your real one)
    const categoryRes = await PutAccommodationCategory(
      {
        id: Number(category?.id),
        title: updated?.title,
      },
      true,
    );

    if (categoryRes?.status !== StatusCode.OK) {
      dispatch(showError("Error updating category"));
      return;
    }

    // Update local state
    setAccommodationCategories(prev =>
      prev?.map(category => (category?.id === updated?.id ? { ...category, title: updated?.title } : category)),
    );

    dispatch(showSuccess(t("Successfully updated category")));
    setEditingCategoryId(null);
    setEditedTitle("");
  };

  const handleCancelEdit = () => {
    setEditingCategoryId(null);
    setEditedTitle("");
  };

  const handleEditClick = (category: IAccommodationCategory) => {
    setEditingCategoryId(category.id);
    setEditedTitle(category?.title);
  };

  return (
    <Page className="mt-4">
      <Card
        title="Categories"
        subtitle="Manage your accommodation categories here"
        titleActions={[
          {
            action: () => {
              setIsAddingCategory(true);
              setNewCategoryTitle("");
            },
            content: (
              <>
                <FontAwesomeIcon icon={faPlus} style={{ marginRight: "6px" }} />
                {t("New Category")}
              </>
            ),
            buttonType: "primary",
          },
        ]}
      >
        <div style={{ marginTop: "24px" }}>
          <DataTable
            noRoundedTop
            columns={[{ label: t("Title") }, { label: "" }]}
            loading={accommodationCategories === undefined}
            footer={{
              tableLimit: filterState?.limit,
              tableOffset: filterState?.offset,
              handleTableOffset: direction =>
                setFilterState(prev => ({
                  ...prev,
                  offset: direction === "prev" ? prev.offset - prev.limit : prev.offset + prev.limit,
                })),
              disableNextOffset: !accommodationCategories || !(accommodationCategories?.length === filterState?.limit),
            }}
            className="property-categories-table"
          >
            {isAddingCategory && (
              <tr>
                <td>
                  <Input
                    value={newCategoryTitle}
                    id="title"
                    onChange={e => setNewCategoryTitle(e.target.value)}
                    placeholder="Enter category title"
                  />
                </td>
                <td className="text-right">
                  <button onClick={() => setIsAddingCategory(false)} className="add-category-cancel-button">
                    Cancel
                  </button>

                  <button
                    onClick={() => handleSaveNewCategory()}
                    disabled={!newCategoryTitle?.trim()}
                    className={classNames("add-category-save-button", {
                      "add-category-save-button_disabled": !newCategoryTitle?.trim(),
                    })}
                  >
                    Save
                  </button>
                </td>
              </tr>
            )}

            {accommodationCategories?.map((category: IAccommodationCategory, index: number) => {
              const isEditing = editingCategoryId === category.id;

              return (
                <tr
                  key={index}
                  //   className={classNames({ clickable: !isEditing })}
                  // onClick={() => handleEditClick(category)}
                >
                  <td>
                    {isEditing ? (
                      <Input
                        id={`edit-${category?.id}`}
                        value={editedTitle}
                        onChange={e => setEditedTitle(e.target.value)}
                        placeholder="Enter category title"
                        autoFocus
                      />
                    ) : (
                      category?.title
                    )}
                  </td>

                  <td className="text-right">
                    {isEditing ? (
                      <>
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            void handleCancelEdit();
                          }}
                          className="add-category-cancel-button"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            void handleSaveEditedCategory(category);
                          }}
                          disabled={!editedTitle?.trim()}
                          className={classNames("add-category-save-button", {
                            "add-category-save-button_disabled": !editedTitle?.trim(),
                          })}
                        >
                          Save
                        </button>
                      </>
                    ) : (
                      <FontAwesomeIcon
                        icon={faPencil}
                        onClick={e => {
                          e.stopPropagation();
                          handleEditClick(category);
                        }}
                        className="category-edit-icon cursor-pointer"
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </DataTable>
        </div>
      </Card>
    </Page>
  );
}
